/**
 * SuperAdminPage — the platform-plane sections of the super-admin console:
 * the tenant directory (one hierarchical tree; click a tenant to open its
 * workspace), Users, and API Keys. Renders inside SuperAdminShell, the same
 * frame the tenant workspace uses, so moving between them never feels like
 * changing pages.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api';
import { getInitials } from '@/utils/getInitials';
import { useAuth } from '@modules/auth';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import { useTenantPath } from '@/routing/paths';
import superAdminService from '@modules/super_admin/services/superAdminService';
import type {
  Tenant,
  User,
  BatchUserUpload,
  BatchUploadResult,
} from '@modules/super_admin/services/superAdminService';
import { message } from 'antd';
import { MAX_TENANT_DEPTH } from '@/types';
import { ApiKeysOverviewTab } from '@modules/super_admin/components/ApiKeysOverviewTab';
import SuperAdminShell, { type ConsoleNav } from '@modules/super_admin/console/SuperAdminShell';
import TenantSwitcher, { useTenantSwitcherHotkey, SWITCHER_KEY_LABEL, type SwitcherTenant } from '@modules/super_admin/console/TenantSwitcher';
import TenantTreeView, { useTenantTreeRows } from '@modules/super_admin/console/TenantTreeView';
import {
  getTreeCollapsedNodes,
  setTreeCollapsedNodes as persistTreeCollapsedNodes,
  getTreeSelectedTenantId,
  setTreeSelectedTenantId as persistTreeSelectedTenantId,
  getLastWorkspaceSection,
  tenantOwnLogo,
} from '@modules/super_admin/console/consoleState';
import '@modules/super_admin/console/console.css';
import './SuperAdminPage.css';
import '@modules/chatbots/components/BotShareModal.css';

/**
 * Per-tenant manager-panel overview entry, served in one call for the whole
 * tree by GET /super-admin/tenants/cascade-overview. Powers the tree-node
 * badges and the detail-pane summary chips on the Tenant Tree tab.
 */
interface TenantOverview {
  models: { enforced: boolean; differs_from_parent: boolean };
  modules: { enforced: boolean; differs_from_parent: boolean };
  wiki_connected: boolean;
  agents_on: boolean;
  flows_on: boolean;
}

/** Tiny colored chip used by the tree badges + detail-pane summary. */
function OverviewChip({ label, tone, title }: { label: string; tone: 'on' | 'off' | 'warn' | 'info'; title: string }) {
  // Semantic tokens, not literal hex: these chips have to follow the active
  // theme (and any tenant that retints it) like every other status colour in
  // the app. color-mix gives the translucent backgrounds without needing an
  // `-rgb` companion var, which --info and --text-tertiary don't have.
  const tones: Record<string, { token: string }> = {
    on:   { token: 'var(--success)' },
    off:  { token: 'var(--text-tertiary)' },
    warn: { token: 'var(--warning)' },
    info: { token: 'var(--info)' },
  };
  const { token } = tones[tone];
  const c = {
    bg: `color-mix(in srgb, ${token} 15%, transparent)`,
    fg: token,
  };
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '1px 7px', borderRadius: 999,
        fontSize: 10, fontWeight: 600, lineHeight: '16px',
        background: c.bg, color: c.fg, whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/** Compact badge strip for one tenant, rendered inside a tree node row. */
function TreeNodeBadges({ ov }: { ov?: TenantOverview }) {
  const { t } = useLanguage();
  if (!ov) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, marginInlineStart: 6, flexWrap: 'wrap' }}>
      {ov.modules.enforced && <OverviewChip label="⚙⇩" tone="info" title={t('superAdmin.overview.modulesEnforcedTip') || 'Modules enforced on sub-tenants'} />}
      {ov.models.enforced && <OverviewChip label="AI⇩" tone="info" title={t('superAdmin.overview.modelsEnforcedTip') || 'AI model settings enforced on sub-tenants'} />}
      {ov.models.differs_from_parent && <OverviewChip label="AI≠" tone="warn" title={t('superAdmin.overview.modelsDifferTip') || 'AI model settings differ from the parent tenant'} />}
      {ov.modules.differs_from_parent && <OverviewChip label="⚙≠" tone="warn" title={t('superAdmin.overview.modulesDifferTip') || 'Module settings differ from the parent tenant'} />}
      {ov.wiki_connected && <OverviewChip label={t('superAdmin.overview.wikiChip') || 'wiki'} tone="on" title={t('superAdmin.overview.wikiTip') || 'A wiki is connected to this tenant'} />}
      {ov.flows_on && <OverviewChip label={t('superAdmin.overview.flowsChip') || 'flows'} tone="on" title={t('superAdmin.overview.flowsTip') || 'Flows module is on'} />}
    </span>
  );
}


export default function SuperAdminPage() {
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const { isSuperAdmin } = useAuth();
  const { t } = useLanguage();
  // The active section lives in the URL (?tab=) so Back from a workspace —
  // and any bookmark — lands on the right one. Tenants is the front door;
  // deep links to the retired Dashboard / Design System tabs and the old
  // 'tenant-tree' fold into it.
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') || 'tenants';
  const currentTab: ConsoleNav = urlTab === 'users' || urlTab === 'api-keys' ? urlTab : 'tenants';

  // ⌘K tenant switcher — jumps straight into a tenant workspace, preserving
  // the operator's last-used section.
  const [switcherOpen, setSwitcherOpen] = useTenantSwitcherHotkey();

  // Tenant directory state
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  // The last-opened tenant stays highlighted and the branches you expanded
  // stay open across navigation (sessionStorage) — coming Back from a
  // workspace returns you to the same place in the tree.
  const [treeSelectedTenantId, setTreeSelectedTenantIdState] = useState<string | null>(() => getTreeSelectedTenantId());
  const setTreeSelectedTenantId = (id: string | null) => {
    setTreeSelectedTenantIdState(id);
    persistTreeSelectedTenantId(id);
  };
  const [treeCollapsedNodes, setTreeCollapsedNodesState] = useState<Set<string>>(
    () => new Set(getTreeCollapsedNodes()),
  );
  const setTreeCollapsedNodes = (updater: (prev: Set<string>) => Set<string>) => {
    setTreeCollapsedNodesState(prev => {
      const next = updater(prev);
      persistTreeCollapsedNodes([...next]);
      return next;
    });
  };
  const [treeSearchQuery, setTreeSearchQuery] = useState<string>('');

  // The directory renders the SAME tree object as the ⌘K switcher, built from
  // the same tenant list — one shape to learn, not two. Unlike the switcher it
  // collapses, because this is a working surface rather than a jump list.
  const switcherTenants = useMemo<SwitcherTenant[]>(
    () => allTenants.map(x => ({
      tenant_id: x.tenant_id,
      tenant_name: x.tenant_name,
      tenant_code: x.tenant_code,
      parent_tenant_id: x.parent_tenant_id ?? null,
      user_count: x.user_count,
      logo: tenantOwnLogo(x),
    })),
    [allTenants],
  );
  const treeRows = useTenantTreeRows(switcherTenants, treeSearchQuery, treeCollapsedNodes);
  const treeMatchCount = useMemo(
    () => (treeSearchQuery.trim() ? treeRows.filter(r => r.match).length : treeRows.length),
    [treeRows, treeSearchQuery],
  );

  // Coming Back from a workspace should land you on the row you left, not at
  // the top of a long tree. Once the tree has painted, bring the remembered
  // row into the middle of the pane — one time, not on every re-render.
  const directoryScrollRef = useRef<HTMLDivElement>(null);
  const scrolledToSelectionRef = useRef(false);
  useEffect(() => {
    if (scrolledToSelectionRef.current || !treeSelectedTenantId || treeRows.length === 0) return;
    // `[data-tenant-id]` rather than a class: the directory now renders the
    // shared TenantTreeView rows, and the attribute is the part of that
    // contract this lookup actually depends on.
    const row = directoryScrollRef.current?.querySelector<HTMLElement>(
      `[data-tenant-id="${treeSelectedTenantId}"]`,
    );
    if (!row) return;
    row.scrollIntoView({ block: 'center' });
    scrolledToSelectionRef.current = true;
  }, [treeRows, treeSelectedTenantId]);

  // Manager-panel overview: cascade enforcement/drift + module + wiki markers
  // for every tenant in ONE call. Powers the per-node badges in the tree and
  // the summary chips above the detail pane. Refreshed on tab entry and on
  // selection change (panel edits below may flip enforcement/drift).
  const [cascadeOverview, setCascadeOverview] = useState<Record<string, TenantOverview>>({});
  const reloadCascadeOverview = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/tenants/cascade-overview');
      setCascadeOverview(res.data?.tenants || {});
    } catch {
      // Badges are progressive enhancement — the tree works without them.
    }
  }, []);
  useEffect(() => {
    if (currentTab === 'tenants') reloadCascadeOverview();
  }, [currentTab, treeSelectedTenantId, reloadCascadeOverview]);

  // Users tab state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Modal state
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  const [createTenantForm, setCreateTenantForm] = useState({
    name: '',
    code: '',
    subdomain: '',
    subscription_tier: 'starter',
    parent_tenant_id: '',
  });
  const [createUserForm, setCreateUserForm] = useState({
    tenant_id: '',
    email: '',
    full_name: '',
    role: 'user',
    password: '',
  });
  // Batch upload state
  const [batchUploadJson, setBatchUploadJson] = useState<string>('');
  const [batchUploadLoading, setBatchUploadLoading] = useState(false);
  const [batchUploadResult, setBatchUploadResult] = useState<BatchUploadResult | null>(null);
  const [showBatchUploadSection, setShowBatchUploadSection] = useState(false);

  // Parent tenant dropdown state
  const [parentTenantSearchQuery, setParentTenantSearchQuery] = useState('');
  const [isParentDropdownOpen, setIsParentDropdownOpen] = useState(false);
  const parentDropdownRef = useRef<HTMLDivElement>(null);

  // Check super admin access
  useEffect(() => {
    if (!isSuperAdmin) {
      console.log('[SUPER_ADMIN] Access denied - redirecting to home');
      navigate('/');
    }
  }, [isSuperAdmin, navigate]);

  // Tenants load on mount — the directory needs them, and so does the
  // Create User modal's tenant picker.
  useEffect(() => {
    loadTenants();
  }, []);

  // Close parent dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target as Node)) {
        setIsParentDropdownOpen(false);
      }
    };

    if (isParentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isParentDropdownOpen]);

  // Build full hierarchy path for a tenant (e.g., "Root / Child1 / Child2")
  const buildTenantHierarchyPath = (tenant: Tenant): string => {
    const path: string[] = [];
    let currentTenant: Tenant | undefined = tenant;

    // Walk up the parent chain to build the path
    while (currentTenant) {
      path.unshift(currentTenant.tenant_name);
      if (currentTenant.parent_tenant_id) {
        currentTenant = allTenants.find(t => t.tenant_id === currentTenant?.parent_tenant_id);
      } else {
        break;
      }
    }

    return path.join(' / ');
  };

  // Get depth icon/indicator based on tenant depth
  const getDepthIndicator = (depth: number): string => {
    const icons = ['🏢', '🏬', '🏪', '🏠', '📍'];
    return icons[Math.min(depth, icons.length - 1)];
  };

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      // One request: the tenant list carries parent_tenant_id, which is all
      // the tree needs. The separate hierarchy endpoint was a second source of
      // truth for the same shape (and returned a synthetic root the list has
      // no notion of).
      const response = await superAdminService.getTenants({ per_page: 1000 });
      setAllTenants(response.tenants);
    } catch (error) {
      console.error('[ERROR] Failed to load tenants:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await superAdminService.getUsers({ per_page: 100 });
      setAllUsers(response.users);
    } catch (error) {
      console.error('[ERROR] Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Tab data loads on ACTIVATION (not just click) so ?tab= deep links —
  // including the workspace back button — land on a populated tab.
  useEffect(() => {
    if (currentTab === 'tenants') loadTenants();
    else if (currentTab === 'users') loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Auto-generate tenant code when name changes (with debounce)
  const codeGenerationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTenantNameChange = (name: string) => {
    setCreateTenantForm(prev => ({ ...prev, name }));

    // Clear any pending code generation
    if (codeGenerationTimeoutRef.current) {
      clearTimeout(codeGenerationTimeoutRef.current);
    }

    if (name.trim()) {
      // Debounce: wait 300ms after user stops typing
      codeGenerationTimeoutRef.current = setTimeout(async () => {
        try {
          const generatedCode = await superAdminService.generateTenantCode(name);
          setCreateTenantForm(prev => ({ ...prev, code: generatedCode }));
        } catch (error) {
          console.error('[ERROR] Failed to generate tenant code:', error);
        }
      }, 300);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...createTenantForm,
        parent_tenant_id: createTenantForm.parent_tenant_id || undefined,
      };
      await superAdminService.createTenant(payload);
      setShowCreateTenantModal(false);
      setCreateTenantForm({
        name: '',
        code: '',
        subdomain: '',
        subscription_tier: 'starter',
        parent_tenant_id: '',
      });
      if (currentTab === 'tenants') {
        loadTenants();
      }
    } catch (error) {
      console.error('[ERROR] Failed to create tenant:', error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!createUserForm.full_name || !createUserForm.full_name.trim()) {
      alert(t('superAdmin.page.fullNameRequired'));
      return;
    }
    if (!createUserForm.email || !createUserForm.email.trim()) {
      alert('Email is required');
      return;
    }
    if (!createUserForm.password || !createUserForm.password.trim()) {
      alert('Password is required');
      return;
    }
    if (!createUserForm.tenant_id) {
      alert('Tenant is required');
      return;
    }

    try {
      await superAdminService.createUser(createUserForm);
      setShowCreateUserModal(false);
      setCreateUserForm({
        tenant_id: '',
        email: '',
        full_name: '',
        role: 'user',
        password: '',
      });
      if (currentTab === 'users') {
        loadUsers();
      }
    } catch (error: any) {
      console.error('[ERROR] Failed to create user:', error);
      const errorMsg = error?.response?.data?.error || t('superAdmin.page.failedToCreateUser');
      alert(errorMsg);
    }
  };


  const handleUserTierChange = async (userId: string, tier: number) => {
    try {
      await superAdminService.updateUser(userId, { user_tier: tier } as Partial<User>);
      message.success(t('superAdmin.page.userTierUpdated'));
      loadUsers();
    } catch (error) {
      console.error('[ERROR] Failed to update user tier:', error);
      message.error(t('superAdmin.page.userTierFailed'));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('superAdmin.page.confirmDeleteUser'))) {
      return;
    }

    try {
      await superAdminService.deleteUser(userId);
      message.success(t('superAdmin.page.userDeleted'));
      loadUsers();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to delete user:', error);
      let detail = t('common.unknownError');
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
        detail = axiosError.response?.data?.error || axiosError.message || detail;
      }
      message.error(`Failed to delete user: ${detail}`);
    }
  };

  const downloadJsonTemplate = () => {
    const template: BatchUserUpload[] = [
      {
        tenant_name: 'Example Tenant',
        email: 'user1@example.com',
        password: 'SecurePass123!',
        full_name: 'John Doe',
        role: 'user',
      },
      {
        tenant_name: 'Example Tenant',
        email: 'user2@example.com',
        password: 'SecurePass456!',
        full_name: 'Jane Smith',
        role: 'admin',
      },
    ];
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_user_upload_template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBatchUpload = async () => {
    if (!batchUploadJson.trim()) {
      message.error(t('superAdmin.page.provideJson'));
      return;
    }

    try {
      const users = JSON.parse(batchUploadJson) as BatchUserUpload[];
      if (!Array.isArray(users)) {
        message.error(t('superAdmin.page.jsonMustBeArray'));
        return;
      }

      setBatchUploadLoading(true);
      const result = await superAdminService.batchUploadUsers(users);
      setBatchUploadResult(result);

      if (result.success) {
        message.success(`Successfully created ${result.successful_count} user(s)`);
        loadUsers();
          setBatchUploadJson('');
      } else if (result.successful_count > 0) {
        message.warning(
          `Partial success: ${result.successful_count} created, ${result.failed_count} failed`
        );
        loadUsers();
        } else {
        message.error(`Failed to create users: ${result.failed_count} error(s)`);
      }
    } catch (error: any) {
      console.error('[ERROR] Batch upload failed:', error);
      if (error.response?.data?.validation_errors) {
        const errors = error.response.data.validation_errors.join('\n');
        message.error(t('superAdmin.page.schemaValidationFailed', { errors }));
      } else {
        message.error(error.response?.data?.error || t('superAdmin.page.failedToUploadUsers'));
      }
    } finally {
      setBatchUploadLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setBatchUploadJson(content);
      } catch (error) {
        message.error(t('superAdmin.page.fileReadFailed'));
      }
    };
    reader.readAsText(file);
  };


  return (
    <SuperAdminShell
      active={currentTab}
      headerActions={(
        <button
          type="button"
          className="sa-directory-btn"
          onClick={() => setSwitcherOpen(true)}
          data-testid="open-tenant-switcher"
        >
          {t('superAdmin.console.switchTenant')} <span className="sa-ctxbar-kbd">{SWITCHER_KEY_LABEL}</span>
        </button>
      )}
    >
          {/* Tenants — ONE hierarchical tree. The card fills the body; its
              header and search stay put while the tree scrolls inside.
              Clicking a tenant opens its workspace — the only action here.
              The old table lens, tree lens and side summary are gone: the
              workspace is the one home for everything per-tenant. */}
          {currentTab === 'tenants' && (
            <section className="sa-directory" data-testid="tenant-directory">
              <div className="sa-directory-head">
                <div className="sa-directory-heading">
                  <h3 className="sa-directory-title">{t('superAdmin.directory.title')}</h3>
                  <p className="sa-directory-hint">{t('superAdmin.directory.treeHint')}</p>
                </div>
                <div className="sa-directory-tools">
                  <label className="sa-directory-search">
                    <svg className="sa-directory-search-icon" viewBox="0 0 24 24" aria-hidden>
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <input
                      type="search"
                      className="sa-directory-search-input"
                      placeholder={t('superAdmin.directory.searchPlaceholder')}
                      value={treeSearchQuery}
                      onChange={(e) => setTreeSearchQuery(e.target.value)}
                      aria-label={t('superAdmin.directory.searchPlaceholder')}
                      data-testid="directory-search"
                    />
                  </label>
                  {treeSearchQuery.trim() && (
                    <span className="sa-directory-count" data-testid="directory-match-count">
                      {t('superAdmin.directory.matches').replace('{{count}}', String(treeMatchCount))}
                    </span>
                  )}
                  <button
                    type="button"
                    className="sa-directory-btn"
                    onClick={loadTenants}
                    title={t('superAdmin.directory.refresh')}
                    aria-label={t('superAdmin.directory.refresh')}
                    data-testid="directory-refresh"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden>
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                      <path d="M8 16l-5 5v-5h5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="sa-directory-btn sa-directory-btn--primary"
                    onClick={() => setShowCreateTenantModal(true)}
                    data-testid="directory-new-tenant"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden>
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    {t('superAdmin.directory.newTenant')}
                  </button>
                </div>
              </div>

              <div className="sa-directory-scroll" data-testid="tenant-directory-tree" ref={directoryScrollRef} role="tree">
                {tenantsLoading ? (
                  <div className="sa-directory-empty">{t('superAdmin.directory.loading')}</div>
                ) : treeRows.length > 0 ? (
                  <TenantTreeView
                    rows={treeRows}
                    query={treeSearchQuery}
                    activeTenantId={treeSelectedTenantId}
                    testIdPrefix="directory-tenant"
                    collapsed={treeCollapsedNodes}
                    onToggleCollapse={(id) => {
                      setTreeCollapsedNodes(prev => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      });
                    }}
                    onSelect={(id) => {
                      // Remember it (the row stays highlighted when you come
                      // Back), then open its workspace on Overview.
                      setTreeSelectedTenantId(id);
                      navigate(tenantPath(`/super-admin/tenants/${id}/overview`));
                    }}
                    renderBadges={(id) => <TreeNodeBadges ov={cascadeOverview[id]} />}
                    renderTrailing={() => (
                      <span className="sa-open-hint" aria-hidden>
                        {t('superAdmin.directory.openHint')} →
                      </span>
                    )}
                  />
                ) : treeSearchQuery.trim() ? (
                  <div className="sa-directory-empty">
                    {t('superAdmin.directory.noMatch').replace('{{query}}', treeSearchQuery.trim())}
                  </div>
                ) : (
                  <div className="sa-directory-empty">{t('superAdmin.directory.empty')}</div>
                )}
              </div>
            </section>
          )}

          {/* Users Tab */}
          {currentTab === 'users' && (
            <div className="tab-content active">
              <div className="header-actions">
                <button
                  className="btn-primary"
                  onClick={() => setShowCreateUserModal(true)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                  {t('admin.createUser')}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setShowBatchUploadSection(!showBatchUploadSection)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {showBatchUploadSection ? t('superAdmin.page.hideBatchUpload') : t('superAdmin.page.showBatchUpload')}
                </button>
                <button className="btn-secondary" onClick={loadUsers}>
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M8 16l-5 5v-5h5" />
                  </svg>
                  {t('admin.refresh')}
                </button>
              </div>

              {/* Batch Upload Section */}
              {showBatchUploadSection && (
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div className="card-header">
                    <h3>{t('superAdmin.page.batchUserUpload')}</h3>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '15px' }}>
                      <button
                        className="btn-secondary"
                        onClick={downloadJsonTemplate}
                        style={{ marginInlineEnd: '10px' }}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" style={{ marginInlineEnd: '5px' }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {t('superAdmin.downloadJsonTemplate')}
                      </button>
                      <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                        <input
                          type="file"
                          accept=".json"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                        <svg viewBox="0 0 24 24" width="16" height="16" style={{ marginInlineEnd: '5px' }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {t('superAdmin.uploadJsonFile')}
                      </label>
                    </div>
                    <textarea dir="auto"
                      value={batchUploadJson}
                      onChange={(e) => setBatchUploadJson(e.target.value)}
                      placeholder={t('superAdmin.page.pasteJsonPlaceholder')}
                      style={{
                        width: '100%',
                        minHeight: '200px',
                        padding: '10px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '15px',
                      }}
                    />
                    <button
                      className="btn-primary"
                      onClick={handleBatchUpload}
                      disabled={batchUploadLoading || !batchUploadJson.trim()}
                    >
                      {batchUploadLoading ? t('common.uploading') : t('admin.uploadUsers')}
                    </button>
                    {batchUploadResult && (
                      <div style={{ marginTop: '20px' }}>
                        <h4>{t('superAdmin.page.uploadResults')}</h4>
                        <p>
                          {t('superAdmin.page.batchTotals', { total: batchUploadResult.total, ok: batchUploadResult.successful_count, failed: batchUploadResult.failed_count })}
                        </p>
                        {batchUploadResult.failed.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <h5>{t('superAdmin.page.failedUsers')}</h5>
                            <ul>
                              {batchUploadResult.failed.map((failure, idx) => (
                                <li key={idx} style={{ color: 'red' }}>
                                  {t('superAdmin.page.batchFailureRow', { index: failure.index, email: failure.email, tenant: failure.tenant_name, error: failure.error })}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {batchUploadResult.successful.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <h5>{t('superAdmin.page.successfulUsers')}</h5>
                            <ul>
                              {batchUploadResult.successful.map((success, idx) => (
                                <li key={idx} style={{ color: 'green' }}>
                                  {t('superAdmin.page.batchSuccessRow', { index: success.index, email: success.email, tenant: success.tenant_name })}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>{t('superAdmin.page.colUser')}</th>
                      <th>{t('superAdmin.page.colTenant')}</th>
                      <th>{t('superAdmin.page.colRole')}</th>
                      <th>{t('superAdmin.page.colTier')}</th>
                      <th>{t('superAdmin.page.colStatus')}</th>
                      <th>{t('superAdmin.page.colLastLogin')}</th>
                      <th>{t('superAdmin.page.colCreated')}</th>
                      <th>{t('superAdmin.page.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan={8} className="loading-cell">
                          {t('admin.loadingUsers')}
                        </td>
                      </tr>
                    ) : allUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="loading-cell">
                          {t('admin.noUsersFound')}
                        </td>
                      </tr>
                    ) : (
                      allUsers.map((user) => (
                        <tr key={user.user_id}>
                          <td>
                            <div className="user-info">
                              <div className="user-avatar-small" dir="auto">
                                {getInitials(user.full_name || user.email)}
                              </div>
                              <div>
                                <div className="user-name" dir="auto">
                                  {user.full_name || user.email}
                                </div>
                                <div className="user-email-small">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="tenant-code-small" dir="auto">{user.tenant.tenant_name}</div>
                          </td>
                          <td>
                            <span className={`role-badge role-${user.role}`} dir="auto">
                              {user.role}
                            </span>
                          </td>
                          <td>
                            <select
                              className="tier-select"
                              value={user.user_tier || 1}
                              onChange={(e) => handleUserTierChange(user.user_id, Number(e.target.value))}
                            >
                              <option value={1}>Tier 1</option>
                              <option value={2}>Tier 2</option>
                              <option value={3}>Tier 3</option>
                              <option value={4}>Tier 4</option>
                              <option value={5}>Tier 5</option>
                            </select>
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                user.is_active ? 'badge-success' : 'badge-error'
                              }`}
                            >
                              {user.is_active ? t('common.active') : t('common.inactive')}
                            </span>
                          </td>
                          <td className="user-date">
                            {user.last_login_at
                              ? new Date(user.last_login_at).toLocaleDateString(activeLocale())
                              : 'Never'}
                          </td>
                          <td className="user-date">
                            {new Date(user.created_at).toLocaleDateString(activeLocale())}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon"
                                onClick={() => handleDeleteUser(user.user_id)}
                                title={t('superAdmin.page.deleteUser')}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* API Keys Tab — global overview of every tenant AI provider
              key + current incidents (unhealthy keys). */}
          {currentTab === 'api-keys' && (
            <div className="tab-content active">
              <ApiKeysOverviewTab />
            </div>
          )}



      {/* Create Tenant Modal */}
      {showCreateTenantModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateTenantModal(false)}
        >
          <div className="modal share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('superAdmin.page.createNewTenant')}</h3>
              <button
                className="close-btn"
                onClick={() => setShowCreateTenantModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTenant}>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="parent-tenant">{t('superAdmin.page.parentTenant')}</label>
                  <div className="searchable-select" ref={parentDropdownRef}>
                    <button
                      type="button"
                      className={`select-trigger ${isParentDropdownOpen ? 'open' : ''} ${createTenantForm.parent_tenant_id ? 'has-value' : ''}`}
                      onClick={() => setIsParentDropdownOpen(!isParentDropdownOpen)}
                    >
                      <span dir="auto" className="select-value">
                        {createTenantForm.parent_tenant_id
                          ? (() => {
                              const selectedTenant = allTenants.find(t => t.tenant_id === createTenantForm.parent_tenant_id);
                              return selectedTenant
                                ? `${getDepthIndicator(selectedTenant.depth ?? 0)} ${selectedTenant.tenant_name}`
                                : t('admin.selectParentTenantPlaceholder');
                            })()
                          : t('superAdmin.page.noneRootTenant')}
                      </span>
                      <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isParentDropdownOpen && (
                      <div className="select-dropdown">
                        <div className="select-search">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                          </svg>
                          <input dir="auto"
                            type="text"
                            placeholder={t('superAdmin.page.searchTenantsShort')}
                            value={parentTenantSearchQuery}
                            onChange={(e) => setParentTenantSearchQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="select-options">
                          {/* None option */}
                          <button
                            type="button"
                            className={`select-option ${!createTenantForm.parent_tenant_id ? 'selected' : ''}`}
                            onClick={() => {
                              setCreateTenantForm({ ...createTenantForm, parent_tenant_id: '' });
                              setIsParentDropdownOpen(false);
                              setParentTenantSearchQuery('');
                            }}
                          >
                            <div className="option-avatar tenant">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                              </svg>
                            </div>
                            <div className="option-info">
                              <span dir="auto" className="option-name">
                                {t('superAdmin.page.noneRootTenant')}
                                <span className="option-badge">ROOT</span>
                              </span>
                              <span dir="auto" className="option-meta">{t('superAdmin.page.createAsTopLevel')}</span>
                            </div>
                            {!createTenantForm.parent_tenant_id && (
                              <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>

                          {/* Filtered tenant options */}
                          {allTenants
                            .filter((tenant) => (tenant.depth ?? 0) < MAX_TENANT_DEPTH)
                            .filter((tenant) =>
                              tenant.tenant_name.toLowerCase().includes(parentTenantSearchQuery.toLowerCase()) ||
                              tenant.tenant_code.toLowerCase().includes(parentTenantSearchQuery.toLowerCase())
                            )
                            .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0))
                            .map((tenant) => {
                              const depth = tenant.depth ?? 0;
                              const hierarchyPath = buildTenantHierarchyPath(tenant);
                              return (
                                <button
                                  key={tenant.tenant_id}
                                  type="button"
                                  className={`select-option ${createTenantForm.parent_tenant_id === tenant.tenant_id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setCreateTenantForm({ ...createTenantForm, parent_tenant_id: tenant.tenant_id });
                                    setIsParentDropdownOpen(false);
                                    setParentTenantSearchQuery('');
                                  }}
                                >
                                  <div className="option-avatar tenant">
                                    {getDepthIndicator(depth)}
                                  </div>
                                  <div className="option-info">
                                    <span dir="auto" className="option-name">
                                      {tenant.tenant_name}
                                      <span className="option-badge">Level {depth}</span>
                                    </span>
                                    <span dir="auto" className="option-meta">{hierarchyPath}</span>
                                  </div>
                                  {createTenantForm.parent_tenant_id === tenant.tenant_id && (
                                    <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}

                          {allTenants
                            .filter((tenant) => (tenant.depth ?? 0) < MAX_TENANT_DEPTH)
                            .filter((tenant) =>
                              tenant.tenant_name.toLowerCase().includes(parentTenantSearchQuery.toLowerCase()) ||
                              tenant.tenant_code.toLowerCase().includes(parentTenantSearchQuery.toLowerCase())
                            ).length === 0 && parentTenantSearchQuery && (
                            <div className="select-no-results">
                              {t('superAdmin.page.noParentMatch', { query: parentTenantSearchQuery })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('superAdmin.page.parentOptionalHint', { max: MAX_TENANT_DEPTH })}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-name">{t('superAdmin.page.tenantName')}</label>
                  <input dir="auto"
                    type="text"
                    id="tenant-name"
                    className="input"
                    required
                    placeholder={t('superAdmin.page.tenantNamePlaceholder')}
                    value={createTenantForm.name}
                    onChange={(e) => handleTenantNameChange(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-code">{t('superAdmin.page.tenantCode')}</label>
                  <input dir="auto"
                    type="text"
                    id="tenant-code"
                    className="input"
                    required
                    placeholder="acme-corp"
                    pattern="[a-z0-9-]+"
                    value={createTenantForm.code}
                    onChange={(e) =>
                      setCreateTenantForm({ ...createTenantForm, code: e.target.value })
                    }
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('superAdmin.autoGeneratedFromNameEdit')}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="subdomain">{t('superAdmin.page.subdomain')}</label>
                  <input dir="auto"
                    type="text"
                    id="subdomain"
                    className="input"
                    placeholder="acme"
                    value={createTenantForm.subdomain}
                    onChange={(e) =>
                      setCreateTenantForm({
                        ...createTenantForm,
                        subdomain: e.target.value,
                      })
                    }
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('superAdmin.optionalSubdomainForTenant')}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="subscription-tier">{t('superAdmin.page.subscriptionTier')}</label>
                  <select
                    id="subscription-tier"
                    className="input"
                    value={createTenantForm.subscription_tier}
                    onChange={(e) =>
                      setCreateTenantForm({
                        ...createTenantForm,
                        subscription_tier: e.target.value,
                      })
                    }
                  >
                    <option value="trial">{t('superAdmin.page.tierTrial')}</option>
                    <option value="starter">{t('superAdmin.page.tierStarter')}</option>
                    <option value="professional">{t('superAdmin.page.tierProfessional')}</option>
                    <option value="enterprise">{t('superAdmin.page.tierEnterprise')}</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateTenantModal(false)}
                >
                  {t('vault.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('groupAdmin.tenants.createTenant')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('superAdmin.page.createNewUser')}</h3>
              <button
                className="close-btn"
                onClick={() => setShowCreateUserModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="user-tenant">{t('superAdmin.page.colTenant')}</label>
                  <select
                    id="user-tenant"
                    className="input"
                    required
                    value={createUserForm.tenant_id}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        tenant_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t('superAdmin.page.selectTenant')}</option>
                    {allTenants.map((tenant) => (
                      <option key={tenant.tenant_id} value={tenant.tenant_id} dir="auto">
                        {tenant.tenant_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="user-email">{t('superAdmin.page.emailAddress')}</label>
                  <input dir="auto"
                    type="email"
                    id="user-email"
                    className="input"
                    required
                    placeholder="user@example.com"
                    value={createUserForm.email}
                    onChange={(e) =>
                      setCreateUserForm({ ...createUserForm, email: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="user-full-name">{t('superAdmin.page.fullName')}</label>
                  <input dir="auto"
                    type="text"
                    id="user-full-name"
                    className="input"
                    required
                    placeholder={t('superAdmin.page.fullNamePlaceholder')}
                    value={createUserForm.full_name}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        full_name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="user-role">{t('superAdmin.page.colRole')}</label>
                  <select
                    id="user-role"
                    className="input"
                    required
                    value={createUserForm.role}
                    onChange={(e) =>
                      setCreateUserForm({ ...createUserForm, role: e.target.value })
                    }
                  >
                    <option value="user">{t('superAdmin.page.roleUser')}</option>
                    <option value="admin">{t('superAdmin.page.roleAdmin')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="user-password">{t('superAdmin.page.password')}</label>
                  <input dir="auto"
                    type="password"
                    id="user-password"
                    className="input"
                    required
                    placeholder={t('superAdmin.page.passwordPlaceholder')}
                    minLength={8}
                    value={createUserForm.password}
                    onChange={(e) =>
                      setCreateUserForm({ ...createUserForm, password: e.target.value })
                    }
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('admin.minimumCharacters')}
                  </small>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateUserModal(false)}
                >
                  {t('vault.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('admin.createUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ⌘K tenant switcher — jumps into a tenant workspace, landing on
          the operator's last-used section (switch preserves place). */}
      <TenantSwitcher
        tenants={switcherTenants}
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        onSelect={(id) => navigate(tenantPath(`/super-admin/tenants/${id}/${getLastWorkspaceSection()}`))}
      />
    </SuperAdminShell>
  );
}
